/**
 * Self-contained styling for the PUBLIC patient portal. Kept inline (like
 * PublicEncounterViewPage's PublicStyles) so the portal reads like a friendly
 * patient site rather than the staff console, and carries no dependency on the
 * staff AppShell / sidebar chrome.
 *
 * ADR-0026: the class contract (`.pp-*`) is consumed verbatim by
 * PortalLoginPage / PortalDashboardPage, so the selectors are preserved; the
 * values are rebranded onto the shadcn/ui design tokens — orange `#ff9900`
 * primary, navy ink (`--foreground`), Inter, crisp `--radius`, and the
 * background/card/muted/border/destructive HSL variables from theme.css.
 */
export function PortalStyles() {
  return (
    <style>{`
      .pp-shell {
        min-height: 100vh;
        background:
          radial-gradient(60% 50% at 50% 0%, hsl(var(--accent)) 0%, transparent 70%),
          hsl(var(--muted));
        color: hsl(var(--foreground));
        font-family: "Inter", system-ui, -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
        padding: 24px 16px 48px;
        box-sizing: border-box;
      }
      .pp-center { display: flex; justify-content: center; }
      .pp-card {
        background: hsl(var(--card));
        border: 1px solid hsl(var(--border));
        border-radius: calc(var(--radius) + 4px);
        box-shadow: 0 1px 2px rgba(16, 33, 61, 0.04),
          0 8px 24px rgba(16, 33, 61, 0.06);
        padding: 24px;
        box-sizing: border-box;
      }
      .pp-login-card { width: 100%; max-width: 420px; margin-top: 6vh; }
      .pp-brand { text-align: center; margin-bottom: 18px; }
      .pp-brand-mark {
        display: inline-flex; align-items: center; justify-content: center;
        width: 52px; height: 52px; border-radius: calc(var(--radius) + 6px);
        background: hsl(var(--primary)); color: hsl(var(--primary-foreground));
        font-size: 26px; margin-bottom: 10px;
      }
      .pp-brand h1 { font-size: 20px; font-weight: 700; margin: 4px 0 2px; }
      .pp-brand p { color: hsl(var(--muted-foreground)); font-size: 13px; margin: 0; }
      .pp-clinic-name { font-size: 18px; font-weight: 700; color: hsl(var(--primary)); }

      .pp-field { margin-bottom: 14px; }
      .pp-label {
        display: block; font-size: 13px; font-weight: 600;
        color: hsl(var(--foreground)); margin-bottom: 6px;
      }
      .pp-input {
        width: 100%; box-sizing: border-box; padding: 10px 12px;
        background: hsl(var(--background));
        border: 1px solid hsl(var(--input)); border-radius: var(--radius);
        font-size: 15px; color: hsl(var(--foreground));
        outline: none; transition: border-color .15s, box-shadow .15s;
      }
      .pp-input::placeholder { color: hsl(var(--muted-foreground)); }
      .pp-input:focus {
        border-color: hsl(var(--ring));
        box-shadow: 0 0 0 3px hsl(var(--ring) / .25);
      }
      .pp-otp { letter-spacing: 8px; text-align: center; font-size: 22px; font-weight: 700; }

      .pp-btn {
        width: 100%; box-sizing: border-box; padding: 11px 14px;
        border: 1px solid transparent; border-radius: var(--radius);
        font-size: 15px; font-weight: 600; cursor: pointer;
        background: hsl(var(--primary)); color: hsl(var(--primary-foreground));
        transition: background .15s, opacity .15s;
      }
      .pp-btn:hover:not(:disabled) { background: hsl(var(--primary) / .9); }
      .pp-btn:disabled { opacity: .6; cursor: not-allowed; }
      .pp-btn-ghost {
        background: hsl(var(--background)); color: hsl(var(--foreground));
        border: 1px solid hsl(var(--border));
      }
      .pp-btn-ghost:hover:not(:disabled) { background: hsl(var(--accent)); }
      .pp-btn-link {
        background: none; border: none; color: hsl(var(--primary)); cursor: pointer;
        font-size: 13px; padding: 4px 0; text-decoration: underline; width: auto;
      }
      .pp-btn-row { display: flex; gap: 10px; margin-top: 4px; }
      .pp-btn-row .pp-btn { width: auto; flex: 1; }

      .pp-hint {
        margin-top: 8px; font-size: 13px; color: hsl(var(--foreground));
        background: hsl(var(--accent)); border: 1px dashed hsl(var(--primary) / .5);
        border-radius: var(--radius); padding: 8px 10px;
      }
      .pp-hint b { letter-spacing: 2px; color: hsl(var(--primary)); }
      .pp-error {
        margin-top: 12px; font-size: 13px; color: hsl(var(--destructive));
        background: hsl(var(--destructive) / .08);
        border: 1px solid hsl(var(--destructive) / .35);
        border-radius: var(--radius); padding: 10px 12px;
      }
      .pp-muted { color: hsl(var(--muted-foreground)); font-size: 13px; }
      .pp-note { font-size: 13px; color: hsl(var(--muted-foreground)); margin: 6px 0 0; }

      /* --- Dashboard --- */
      .pp-wrap { max-width: 860px; margin: 0 auto; }
      .pp-topbar {
        display: flex; align-items: center; justify-content: space-between;
        gap: 12px; margin-bottom: 18px; flex-wrap: wrap;
      }
      .pp-topbar h1 { font-size: 18px; font-weight: 700; margin: 0; }
      .pp-topbar .pp-clinic-name { font-size: 14px; }
      .pp-hello { color: hsl(var(--muted-foreground)); font-size: 13px; }
      .pp-logout {
        background: hsl(var(--card)); border: 1px solid hsl(var(--border));
        border-radius: var(--radius);
        padding: 8px 12px; font-size: 13px; font-weight: 600; cursor: pointer;
        color: hsl(var(--foreground)); width: auto;
      }
      .pp-logout:hover { background: hsl(var(--muted)); }

      .pp-tabs {
        display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap;
      }
      .pp-tab {
        background: hsl(var(--card)); border: 1px solid hsl(var(--border));
        border-radius: 999px;
        padding: 8px 16px; font-size: 14px; font-weight: 600; cursor: pointer;
        color: hsl(var(--muted-foreground)); width: auto;
      }
      .pp-tab.active {
        background: hsl(var(--primary)); color: hsl(var(--primary-foreground));
        border-color: hsl(var(--primary));
      }

      .pp-section-title { font-size: 15px; font-weight: 700; margin: 0 0 14px; }
      .pp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px; }
      .pp-grid .pp-kv { min-width: 0; }
      .pp-kv .k { font-size: 12px; color: hsl(var(--muted-foreground)); }
      .pp-kv .v { font-size: 15px; font-weight: 600; word-break: break-word; }

      .pp-list { display: flex; flex-direction: column; gap: 10px; }
      .pp-item {
        border: 1px solid hsl(var(--border)); border-radius: calc(var(--radius) + 2px);
        padding: 12px 14px;
      }
      .pp-item-head {
        display: flex; align-items: baseline; justify-content: space-between;
        gap: 10px; margin-bottom: 4px; flex-wrap: wrap;
      }
      .pp-item-title { font-weight: 700; font-size: 15px; }
      .pp-item-sub { color: hsl(var(--muted-foreground)); font-size: 13px; }
      .pp-badge {
        font-size: 12px; font-weight: 600; padding: 2px 10px; border-radius: 999px;
        background: hsl(var(--accent)); color: hsl(var(--primary));
        white-space: nowrap;
      }
      .pp-badge.ok { background: hsl(142 55% 94%); color: hsl(142 64% 30%); }
      .pp-badge.warn { background: hsl(38 92% 92%); color: hsl(30 80% 38%); }
      .pp-badge.muted {
        background: hsl(var(--muted)); color: hsl(var(--muted-foreground));
      }

      .pp-empty { text-align: center; color: hsl(var(--muted-foreground)); padding: 28px 12px; }

      @media (max-width: 520px) {
        .pp-grid { grid-template-columns: 1fr; }
        .pp-btn-row { flex-direction: column; }
        .pp-btn-row .pp-btn { width: 100%; }
      }
    `}</style>
  );
}
